import { extractPhoneNumberDigits } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { isTestPhoneNumber } from "@src/lib/cognito/src/lib/amplifyAuth";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { type ApiResponse } from "../../api/api";
import { IdentifyProviderEnum } from "./types";
import {
  type OtpRequest,
  type RequestOtpErrorResponse,
  type RequestOtpSuccessResponse,
} from "./useRequestOtp";

export function useRequestAuthOtp(
  options: UseMutationOptions<
    ApiResponse<RequestOtpSuccessResponse>,
    AxiosError<RequestOtpErrorResponse>,
    OtpRequest
  > = {}
): UseMutationResult<
  ApiResponse<RequestOtpSuccessResponse>,
  AxiosError<RequestOtpErrorResponse>,
  OtpRequest
> {
  return useMutation<
    ApiResponse<RequestOtpSuccessResponse>,
    AxiosError<RequestOtpErrorResponse>,
    OtpRequest
  >({
    mutationFn: async ({ phone }) => {
      const phoneNumber = extractPhoneNumberDigits(phone);
      const phoneNumberInE164 = `+1${phoneNumber}`;

      // Test phone numbers (555-01XX range) are pre-created static accounts for
      // Apple App Store reviewers. Skip signup entirely and go straight to the
      // CUSTOM_AUTH + PHONE_OTP sign-in flow — these accounts must already exist
      // in Cognito with a static OTP configured server-side.
      if (isTestPhoneNumber(phoneNumberInE164)) {
        const cognitoAuth = getCognitoSingleton();
        await cognitoAuth.requestPhoneOtp(phoneNumberInE164);
        logEvent(APP_V2_USER_EVENTS.ONBOARDING_IDP_SELECTED, {
          identityProvider: IdentifyProviderEnum.cognito,
          isSignup: false,
        });
        return {
          status: 200,
          statusText: "OK",
          data: {
            message: "otpIsValid",
            identityProvider: IdentifyProviderEnum.cognito,
            isSignup: false,
          },
        } satisfies ApiResponse<RequestOtpSuccessResponse>;
      }

      const cognitoAuth = getCognitoSingleton();

      // Unified signup/login flow: always try signUp first.
      // signUp triggers the PreSignUp Lambda which imports the backend user
      // into Cognito. For existing users, signUp fails (UsernameExistsException,
      // UserLambdaValidationException, etc.) — we catch all errors and fall
      // through to the sign-in path.
      let isNewUser = false;
      try {
        await cognitoAuth.signUp(phoneNumberInE164);
        isNewUser = true;
      } catch {
        // signUp failed — expected for existing users, proceed to sign-in.
      }

      // For NEW users, signUp already sent the confirmation code to the phone.
      // Return immediately — the OTP will be confirmed via confirmSignUp() +
      // autoSignIn() in useValidateAuthOtp.
      if (isNewUser) {
        logEvent(APP_V2_USER_EVENTS.ONBOARDING_IDP_SELECTED, {
          identityProvider: IdentifyProviderEnum.cognito,
          isSignup: true,
        });
        return {
          status: 200,
          statusText: "OK",
          data: {
            message: "otpIsValid",
            identityProvider: IdentifyProviderEnum.cognito,
            isSignup: true,
          },
        } satisfies ApiResponse<RequestOtpSuccessResponse>;
      }

      // For EXISTING users, initiate a sign-in with SMS OTP.
      // The OTP will be confirmed via confirmPhoneOtp() in useValidateAuthOtp.
      try {
        await cognitoAuth.requestPhoneOtp(phoneNumberInE164);
        logEvent(APP_V2_USER_EVENTS.ONBOARDING_IDP_SELECTED, {
          identityProvider: IdentifyProviderEnum.cognito,
          isSignup: false,
        });
        return {
          status: 200,
          statusText: "OK",
          data: {
            message: "otpIsValid",
            identityProvider: IdentifyProviderEnum.cognito,
            isSignup: false,
          },
        } satisfies ApiResponse<RequestOtpSuccessResponse>;
      } catch (error) {
        logError(APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE, {
          error,
          metadata: {
            identityProvider: IdentifyProviderEnum.cognito,
            phoneNumber,
          },
        });
        throw error;
      }
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE,
    },
    ...options,
  });
}
